/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

let themeSlidersBootstrapped = false;

function shouldWaitForRelatedProducts() {
  return (
    window.Theme?.template?.name === 'product' &&
    !!document.querySelector('.product-recommendations[data-url]')
  );
}

function waitForRelatedProducts() {
  if (!shouldWaitForRelatedProducts()) {
    return Promise.resolve();
  }

  if (window.__relatedProductsReady) {
    return window.__relatedProductsReady;
  }

  return new Promise((resolve) => {
    let attempts = 0;

    const poll = () => {
      if (window.__relatedProductsReady) {
        window.__relatedProductsReady.then(resolve);
        return;
      }

      attempts += 1;
      if (attempts > 100) {
        resolve();
        return;
      }

      requestAnimationFrame(poll);
    };

    poll();
  });
}

function getMaxSlidesPerView(options) {
  const values = [Number(options?.slidesPerView)].filter(Number.isFinite);

  if (options?.breakpoints && typeof options.breakpoints === 'object') {
    Object.values(options.breakpoints).forEach((breakpointOptions) => {
      const slidesPerView = Number(breakpointOptions?.slidesPerView);
      if (Number.isFinite(slidesPerView)) {
        values.push(slidesPerView);
      }
    });
  }

  return values.length ? Math.max(...values) : 1;
}

function shouldEnableLoop(slideCount, options) {
  if (slideCount < 2 || options.loop === false) {
    return false;
  }

  const maxSlidesPerView = getMaxSlidesPerView(options);
  const minSlidesForLoop = Math.max(2, Math.ceil(maxSlidesPerView * 2));

  return slideCount >= minSlidesForLoop;
}

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation, but allow retry after a failed init
    if (initializedSliders.has(element)) {
      const existing = sliderInstances.get(element);
      if (existing?.isInitialized) return;
      initializedSliders.delete(element);
    }
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    this.handleResize = this.handleResize.bind(this);
  }

  attachResizeListener() {
    if (this.resizeListenerAttached) return;
    window.addEventListener('resize', this.handleResize);
    this.resizeListenerAttached = true;
  }

  detachResizeListener() {
    if (!this.resizeListenerAttached) return;
    window.removeEventListener('resize', this.handleResize);
    this.resizeListenerAttached = false;
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    const isElementHidden = this.element.classList.contains('hidden') || this.element.closest('.hidden');
    const isElementHtmlHidden = this.element.hasAttribute('hidden') || this.element.closest('[hidden]');

    if (isElementHidden || isElementHtmlHidden) {
      return false;
    }
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    if (!this.isInitialized) return;

    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      if (this.swiper && !this.swiper.destroyed) {
        try {
          this.swiper.update();
        } catch (_) {
          // Ignore resize update failures from partially initialized loop sliders.
        }
      }

      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) {
      initializedSliders.delete(this.element);
      return;
    }

    // Find and preserve control elements before clearing
    // First try within the slider (nested), then try in parent section (separate blocks)
    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');
    
    if (!controlsElement && blockId) {
      // Look for controls in the same section that target this slider
      const section = this.element.closest('[id^="shopify-section-"]');
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
      }
      
      // If still not found, try any controls without a specific slider ID in the section
      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          // Only auto-match if there's exactly one slider and one unassigned controls block
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        initializedSliders.delete(this.element);
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('swiper', 'slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider
    // (If controls are outside in the section, they stay where they are)
    const wasNested = controlsElement?.parentElement === this.contentEl;
    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    let customOptions = {};
    if (this.element.dataset.swiperOptions) {
      try {
        customOptions = JSON.parse(this.element.dataset.swiperOptions);
      } catch (error) {
        // Keep slider functional even when an option string is malformed.
        customOptions = {};
      }
    }

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    const enableLoop = shouldEnableLoop(slides.length, customOptions);

    defaultOptions.loop = enableLoop;
    customOptions.loop = enableLoop;
    customOptions.watchOverflow = true;

    if (enableLoop) {
      customOptions.loopedSlides = slides.length;
      customOptions.loopAdditionalSlides = slides.length;
    } else {
      delete customOptions.loopedSlides;
      delete customOptions.loopAdditionalSlides;
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      let navSelector;
      
      if (controlsElement) {
        // Controls exist - use them for navigation
        if (controlsElement.dataset.sliderId === blockId) {
          // Controls are separate and explicitly linked
          navSelector = `[data-slider-control][data-slider-id="${blockId}"]`;
        } else if (!controlsElement.dataset.sliderId) {
          // Controls are nested or auto-matched, use the controls element directly
          // Don't use a selector string, pass the actual element's navigation buttons
          const nextBtn = controlsElement.querySelector('.slider-navigation-next');
          const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
          if (nextBtn && prevBtn) {
            customOptions.navigation = {
              nextEl: nextBtn,
              prevEl: prevBtn,
            };
          }
        }
      }
      
      // If navSelector is set (separate controls), use selector strings
      if (navSelector) {
        customOptions.navigation = {
          nextEl: `${navSelector} .slider-navigation-next`,
          prevEl: `${navSelector} .slider-navigation-prev`,
        };
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    // Drop string/false pagination config from block options; controls pass live elements.
    if (
      customOptions.pagination === false ||
      (controlsElement && customOptions.pagination?.el && typeof customOptions.pagination.el === 'string')
    ) {
      delete customOptions.pagination;
    }

    // Drop invalid pagination/navigation targets before Swiper mount.
    if (customOptions.pagination?.el && !(customOptions.pagination.el instanceof Element)) {
      delete customOptions.pagination;
    }

    if (customOptions.navigation) {
      const { nextEl, prevEl } = customOptions.navigation;
      if (!(nextEl instanceof Element) || !(prevEl instanceof Element)) {
        delete customOptions.navigation;
      }
    }

    // Initialize Swiper
    try {
      this.swiper = new Swiper(swiperEl, {
        ...defaultOptions,
        ...customOptions,
        on: {
          ...(customOptions.on || {}),
          resize: () => {
            if (!this.swiper || this.swiper.destroyed) return;

            try {
              this.swiper.update();
            } catch (_) {
              // Ignore resize update failures from partially initialized loop sliders.
            }

            customOptions.on?.resize?.(this.swiper);
          },
        },
      });

      this.isInitialized = true;
      this.attachResizeListener();
    } catch (error) {
      try {
        this.swiper = new Swiper(swiperEl, {
          ...defaultOptions,
          loop: false,
          watchOverflow: true,
        });
        this.isInitialized = true;
        this.attachResizeListener();
        return;
      } catch (_) {}

      initializedSliders.delete(this.element);
      sliderInstances.delete(this.element);
      this.detachResizeListener();

      if (this.swiperEl?.classList.contains('swiper-initialized')) {
        try {
          this.swiper?.destroy(true, true);
        } catch (_) {}
      }

      this.swiper = null;
      this.destroy();

      if (this.element.closest('.product-recommendations') && !this._relatedInitRetried) {
        this._relatedInitRetried = true;
        initializedSliders.delete(this.element);
        sliderInstances.delete(this.element);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.isInitialized) {
              this.init();
            }
          });
        });
      } else if (!this.element.closest('.product-recommendations')) {
        initializedSliders.delete(this.element);
        sliderInstances.delete(this.element);
      }
    }
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    this.detachResizeListener();

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    this.detachResizeListener();
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

function sortSliderBlocks(sliders) {
  return [...sliders].sort((a, b) => {
    const aRelated = a.closest('.product-recommendations') ? 0 : 1;
    const bRelated = b.closest('.product-recommendations') ? 0 : 1;
    return aRelated - bRelated;
  });
}

function whenSwiperReady() {
  if (window.Swiper) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const poll = () => {
      if (window.Swiper) {
        resolve();
        return;
      }

      requestAnimationFrame(poll);
    };

    poll();
  });
}

function whenLayoutReady() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

function whenPageFullyReady() {
  if (document.readyState === 'complete') {
    return whenLayoutReady();
  }

  return new Promise((resolve) => {
    window.addEventListener('load', () => {
      whenLayoutReady().then(resolve);
    }, { once: true });
  });
}

/**
 * Initialize related product sliders only (Professional Pairings).
 */
function initRelatedProductSliders() {
  document.querySelectorAll('.product-recommendations [data-slider-block]').forEach((slider) => {
    const existing = sliderInstances.get(slider);
    if (existing?.isInitialized) return;
    new SliderBlock(slider);
  });

  processPendingInitializations();
}

async function initRelatedProductSlidersWhenReady() {
  await whenSwiperReady();
  await whenPageFullyReady();
  initRelatedProductSliders();
}

/**
 * Initialize slider blocks excluding related product recommendations.
 */
function initNonRelatedSliderBlocks() {
  const sliders = sortSliderBlocks(
    [...document.querySelectorAll('[data-slider-block]')].filter(
      (slider) => !slider.closest('.product-recommendations')
    )
  );

  sliders.forEach((slider) => {
    const existing = sliderInstances.get(slider);
    if (existing?.isInitialized) return;
    new SliderBlock(slider);
  });

  processPendingInitializations();
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = sortSliderBlocks(document.querySelectorAll('[data-slider-block]'));

  sliders.forEach((slider) => {
    const existing = sliderInstances.get(slider);
    if (existing?.isInitialized) return;
    new SliderBlock(slider);
  });

  processPendingInitializations();
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

/**
 * Observe dynamic DOM updates and initialize newly injected slider blocks.
 */
function observeDynamicSliderBlocks() {
  if (!document.body || typeof MutationObserver === 'undefined') return;

  let queued = false;
  const scheduleInit = () => {
    if (!themeSlidersBootstrapped) return;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initSliderBlocks();
      processPendingInitializations();
    });
  };

  const observer = new MutationObserver((mutations) => {
    const hasNewSlider = mutations.some((mutation) => {
      if (!mutation.addedNodes.length) return false;
      return [...mutation.addedNodes].some((node) => {
        if (node.nodeType !== 1) return false;
        if (node.matches?.('[data-slider-block]')) return true;
        return !!node.querySelector?.('[data-slider-block]');
      });
    });

    if (hasNewSlider) {
      scheduleInit();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

// Export before bootstrap so globals exist even if initialization fails
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;
window.initThemeSliders = initSliderBlocks;
window.initRelatedProductSliders = initRelatedProductSliders;
window.initRelatedProductSlidersWhenReady = initRelatedProductSlidersWhenReady;

async function bootstrapThemeSliders() {
  waitForSwiper();
  observeDynamicSliderBlocks();

  await waitForRelatedProducts();

  await whenSwiperReady();

  if (shouldWaitForRelatedProducts()) {
    await whenPageFullyReady();
  } else {
    await whenLayoutReady();
  }

  initNonRelatedSliderBlocks();
  themeSlidersBootstrapped = true;

  if (window.Theme?.template?.name === 'product' && typeof window.initProductMediaSwipers === 'function') {
    requestAnimationFrame(() => {
      window.initProductMediaSwipers();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrapThemeSliders);
} else {
  bootstrapThemeSliders();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

